import { Link, useSearchParams } from "react-router-dom";
import { LogOut, ClipboardList, BookOpen, MessageSquareQuote, Settings, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import { useMyApplications, useMyTrials } from "@/hooks/useMember";
import { useProgrammes } from "@/hooks/useCatalogue";
import ApplicationCard from "@/components/member/ApplicationCard";
import CheckinJournal from "@/components/member/CheckinJournal";
import StoryShare from "@/components/member/StoryShare";
import SettingsPanel from "@/components/member/SettingsPanel";

function nextStep(apps, trials, programmes) {
  const invited = apps.find((a) => a.status === "selected");
  if (invited) return { title: "You've been invited", text: `Accept your invitation to ${invited.programme?.title} and add shipping details.`, cta: "Accept invitation", tab: "programmes" };
  const dispatched = trials.find((t) => t.status === "dispatched");
  if (dispatched) return { title: "Your product is on its way", text: "Once it arrives, confirm delivery to open your private journal.", cta: "Track delivery", tab: "programmes" };
  const inProgress = trials.find((t) => t.status === "in_progress");
  if (inProgress) {
    const done = new Set((inProgress.checkins || []).filter((c) => c.status === "saved").map((c) => c.day));
    const due = inProgress.checkin_days.find((d) => !done.has(d));
    if (due) return { title: `Day ${due} check-in`, text: "A few private notes on texture, comfort and how the ritual fits your day.", cta: "Open journal", tab: "journal" };
    return { title: "All check-ins recorded", text: "Thank you. If you'd like, share a public story — entirely voluntary.", cta: "Share a story", tab: "stories" };
  }
  if (apps.some((a) => ["submitted", "under_review", "waitlisted", "accepted"].includes(a.status)))
    return { title: "No action needed right now", text: "Your application is with us. We'll update its status here and let you know when something changes.", cta: "View applications", tab: "programmes" };
  if (programmes.some((p) => ["open", "waitlist"].includes(p.status)))
    return { title: "A programme is open", text: "Read the eligibility and terms, then apply if it fits. Applying does not guarantee a product.", cta: "Browse programmes", to: "/programmes" };
  return { title: "No action needed right now", text: "When a programme opens or an invitation arrives, it'll appear here first. In the meantime, explore our rituals and the journal.", cta: BRAND.exploreCta, to: "/products" };
}

const triggerCls = "gap-2 rounded-none px-5 py-3 text-[11px] uppercase tracking-[0.2em] data-[state=active]:bg-forest data-[state=active]:text-ivory";

export default function MyRituals() {
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "programmes";
  const { data: apps = [] } = useMyApplications();
  const { data: trials = [] } = useMyTrials();
  const { data: programmes = [] } = useProgrammes();
  const firstName = user?.first_name || user?.display_name || "friend";
  const step = nextStep(apps, trials, programmes);
  const goTab = (t) => setParams({ tab: t });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="my-rituals">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{BRAND.dashboard}</p>
          <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-forest leading-[0.95]" data-testid="my-rituals-greeting">Welcome to Rituals With You, {firstName}</h1>
          <p className="mt-4 text-charcoal/65 font-light">Your private space to track, reflect and share.</p>
        </div>
        <button onClick={logout} data-testid="logout-button" className="btn btn-outline self-start !px-5 !py-3"><LogOut size={14} /> Sign out</button>
      </div>

      <div className="mt-10 relative bg-forest text-ivory p-8 sm:p-10 overflow-hidden grain" data-testid="next-step-card">
        <div className="relative grid sm:grid-cols-12 gap-6 items-end">
          <div className="sm:col-span-8">
            <p className="text-[10px] uppercase tracking-[0.24em] text-champagne">Your next step</p>
            <p className="mt-3 font-serif font-light text-3xl sm:text-4xl leading-tight" data-testid="next-step-title">{step.title}</p>
            <p className="mt-3 text-ivory/70 font-light max-w-lg">{step.text}</p>
          </div>
          <div className="sm:col-span-4 sm:text-right">
            {step.to ? <Link to={step.to} className="btn btn-light" data-testid="next-step-cta">{step.cta} <ArrowRight size={14} /></Link>
              : <button onClick={() => goTab(step.tab)} className="btn btn-light" data-testid="next-step-cta">{step.cta} <ArrowRight size={14} /></button>}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={goTab} className="mt-12">
        <TabsList className="bg-linen flex-wrap h-auto p-1 rounded-none">
          <TabsTrigger value="programmes" data-testid="tab-programmes" className={triggerCls}><ClipboardList size={14} /> Programmes</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal" className={triggerCls}><BookOpen size={14} /> Private journal</TabsTrigger>
          <TabsTrigger value="stories" data-testid="tab-stories" className={triggerCls}><MessageSquareQuote size={14} /> Stories</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className={triggerCls}><Settings size={14} /> Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="programmes" className="mt-8 space-y-6">
          {apps.length === 0 ? (
            <div className="border border-forest/20 p-1"><div className="border border-forest/10 px-8 py-14 text-center" data-testid="applications-empty">
              <p className="font-serif font-light text-3xl text-forest">No applications yet</p>
              <p className="mt-3 text-charcoal/65 max-w-md mx-auto font-light">When a programme opens, you can apply and track its status here — from application to invitation to trial.</p>
              <Link to="/programmes" className="btn btn-primary mt-8" data-testid="browse-programmes-cta">Browse programmes</Link>
            </div></div>
          ) : apps.map((a) => <ApplicationCard key={a.application_id} app={a} />)}
        </TabsContent>

        <TabsContent value="journal" className="mt-8"><CheckinJournal trials={trials} /></TabsContent>
        <TabsContent value="stories" className="mt-8"><StoryShare trials={trials} /></TabsContent>
        <TabsContent value="settings" className="mt-8"><SettingsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
